import { createSelector } from "@reduxjs/toolkit";
import { IIssueBoardApi } from "@mcapps/shared";
import { PostProjectByProjectIdIssuesOnBoardsApiArg } from "../../../../../openapi";
import { selectCurrentProjectId } from "../../../../global/globalSliceCommon";
import { IRootState } from "../../../../store";
import { deduplicatedMembers, selectHttpIssueBoards, selectIssueBoardsWithFavoriteRows } from "../issueSelectors";
import { multiRegisterIssuesApi } from "./multiRegisterIssuesApi";

export const selectSelectedMultiRegisterIssueBoardIds = (state: IRootState) =>
  state.multiRegisterIssuesReducer.selectedIssueBoardIds;

export const selectMultiRegisterIssueBoardIdsFromUrl = (state: IRootState) =>
  state.multiRegisterIssuesReducer.issueBoardIdsFromUrl;

export const selectIssueBoardsTableActiveStatus = (state: IRootState) =>
  state.multiRegisterIssuesReducer.issueBoardsTableActiveStatus;

export const selectMultiRegisterIssuesPage = (state: IRootState) => state.multiRegisterIssuesReducer.page;

export const selectMultiRegisterIssuesPageSize = (state: IRootState) => state.multiRegisterIssuesReducer.pageSize;

export const selectMultiRegisterIssuesSort = (state: IRootState) => state.multiRegisterIssuesReducer.sort;

export const selectMultiRegisterIssuesFilters = (state: IRootState) => state.multiRegisterIssuesReducer.filters;

export const selectMultiRegisterIssuesTaskOrIssueFilter = (state: IRootState) =>
  state.multiRegisterIssuesReducer.filters.taskOrIssue;

export const selectMultiRegisterIssuesFreeTextFilter = (state: IRootState) =>
  state.multiRegisterIssuesReducer.filters.freeText;

export const selectMultiRegisterIssuesIssueBoardIdsFilter = (state: IRootState) =>
  state.multiRegisterIssuesReducer.filters.issueboardIds;

export const selectMultiRegisterIssuesStatusFilter = (state: IRootState) =>
  state.multiRegisterIssuesReducer.filters.status;

export const selectMultiRegisterIssuesAssignedToFilter = (state: IRootState) =>
  state.multiRegisterIssuesReducer.filters.assignedToIds;

export const selectMultiRegisterIssuesAssignedToFilterOptions = createSelector([deduplicatedMembers], (members) =>
  members
    .filter((member) => member.isPassive !== true)
    .map((member) => ({
      label: member.user.name,
      value: member.user._id,
      isActive: false,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "nb", { sensitivity: "base" })),
);

export const selectIssueBoardTableRows = createSelector(
  [selectIssueBoardsWithFavoriteRows, selectSelectedMultiRegisterIssueBoardIds, selectIssueBoardsTableActiveStatus],
  (issueBoards, selectedMultiRegisterIssueBoardIds, activeStatus) => {
    return issueBoards
      .filter((board) => board.status === activeStatus)
      .map((board) => ({
        ...board,
        isSelectedForMultiRegister: selectedMultiRegisterIssueBoardIds.includes(board._id),
      }));
  },
);

export const selectIssueBoardStatusCounts = createSelector([selectIssueBoardsWithFavoriteRows], (issueBoards) => {
  return issueBoards.reduce<Record<string, number>>((acc, board) => {
    acc[board.status] = (acc[board.status] || 0) + 1;
    return acc;
  }, {});
});

export const selectIssueBoardActiveStatusSelection = createSelector(
  [selectIssueBoardsWithFavoriteRows, selectSelectedMultiRegisterIssueBoardIds, selectIssueBoardsTableActiveStatus],
  (issueBoards, selectedMultiRegisterIssueBoardIds, activeStatus) => {
    const activeStatusBoardIds = issueBoards.filter((board) => board.status === activeStatus).map((board) => board._id);

    return {
      activeStatusBoardIds,
      selectedMultiRegisterIssueBoardIds,
      allActiveStatusBoardsSelected:
        activeStatusBoardIds.length > 0 &&
        activeStatusBoardIds.every((boardId) => selectedMultiRegisterIssueBoardIds.includes(boardId)),
      someActiveStatusBoardsSelected: activeStatusBoardIds.some((boardId) =>
        selectedMultiRegisterIssueBoardIds.includes(boardId),
      ),
    };
  },
);

export const selectIssueBoardTablePnsItems = createSelector(
  [(state: IRootState) => state.adminReducer.project?.pns],
  (pnsItems) =>
    pnsItems?.map((pns) => ({
      ...pns,
      comboLabel: `${pns.props.pnsId} - ${pns.props.title}`,
    })) || [],
);

export const selectIssueBoardsUserCanAccessFromUrl = createSelector(
  [selectHttpIssueBoards, selectMultiRegisterIssueBoardIdsFromUrl],
  (issueBoards, issueboardIds): IIssueBoardApi[] => {
    const requestedIssueboardIds = new Set(issueboardIds);
    return issueBoards.filter((issueBoard) => requestedIssueboardIds.has(issueBoard._id));
  },
);

export const selectIssueBoardIdsUserCanAccessFromUrl = createSelector(
  [selectIssueBoardsUserCanAccessFromUrl],
  (issueBoards): string[] => issueBoards.map((issueBoard) => issueBoard._id),
);

export const selectMultiRegisterIssuesActiveIssueBoardIds = createSelector(
  [selectIssueBoardIdsUserCanAccessFromUrl, selectMultiRegisterIssuesIssueBoardIdsFilter],
  (issueboardIdsUserCanAccess, issueboardIdsFilter): string[] => {
    if (issueboardIdsFilter == null) return issueboardIdsUserCanAccess;

    const issueboardIdsUserCanAccessSet = new Set(issueboardIdsUserCanAccess);
    return issueboardIdsFilter.filter((issueboardId) => issueboardIdsUserCanAccessSet.has(issueboardId));
  },
);

export const selectMultiRegisterIssuesQueryArgs = createSelector(
  [
    selectCurrentProjectId,
    selectIssueBoardIdsUserCanAccessFromUrl,
    selectMultiRegisterIssuesActiveIssueBoardIds,
    selectMultiRegisterIssuesPage,
    selectMultiRegisterIssuesPageSize,
    selectMultiRegisterIssuesSort,
    selectMultiRegisterIssuesTaskOrIssueFilter,
    selectMultiRegisterIssuesFreeTextFilter,
    selectMultiRegisterIssuesStatusFilter,
    selectMultiRegisterIssuesAssignedToFilter,
  ],
  (
    projectId,
    issueboardIdsUserCanAccess,
    activeIssueboardIds,
    page,
    pageSize,
    sort,
    taskOrIssue,
    freeText,
    status,
    assignedToIds,
  ): PostProjectByProjectIdIssuesOnBoardsApiArg | null => {
    if (projectId == null || issueboardIdsUserCanAccess.length === 0) return null;
    const trimmedFreeText = freeText.trim();
    const filter = {
      ...(taskOrIssue != null ? { taskOrIssue } : {}),
      ...(trimmedFreeText.length > 0 ? { freeText: trimmedFreeText } : {}),
      ...(status.length > 0 ? { status } : {}),
      ...(assignedToIds.length > 0 ? { assignedToIds } : {}),
      issueboardIds: activeIssueboardIds,
    };

    return {
      projectId,
      body: {
        issueboardIds: issueboardIdsUserCanAccess,
        limit: pageSize,
        page,
        includeIssues: "true",
        includeTasks: "true",
        includeTotalCount: "true",
        filter,
        ...(sort != null ? { sort } : {}),
      },
    };
  },
);

export const selectMultiRegisterIssuesBoardCounts = createSelector(
  [(state: IRootState) => state, selectMultiRegisterIssuesQueryArgs],
  (state, queryArgs) => {
    if (queryArgs == null) return [];
    return (
      multiRegisterIssuesApi.endpoints.getMultiRegisterIssuesOnBoards.select(queryArgs)(state).data?.boardCounts ?? []
    );
  },
);

export const selectMultiRegisterIssueBoardsPanelRows = createSelector(
  [selectIssueBoardsUserCanAccessFromUrl, selectMultiRegisterIssuesBoardCounts],
  (issueBoards, boardCounts) => {
    const boardCountByIssueBoardId = new Map(
      boardCounts.map((boardCount) => [boardCount.issueboardId, boardCount.count]),
    );

    return [...issueBoards]
      .sort((a, b) => a.name.localeCompare(b.name, "nb", { sensitivity: "base" }))
      .map((issueBoard) => ({
        issueBoard,
        count: boardCountByIssueBoardId.get(issueBoard._id) ?? 0,
      }));
  },
);
